import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import FontFamily from "@tiptap/extension-font-family";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
  AlignLeft, AlignCenter, AlignRight, Undo2, Redo2,
} from "lucide-react";
import { cn } from "@/lib/utils";

const FONTS = [
  { label: "Serif (Cormorant)", value: '"Cormorant Garamond", Georgia, serif' },
  { label: "Sans (Manrope)", value: "Manrope, system-ui, sans-serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Times", value: '"Times New Roman", Times, serif' },
];

const COLORS = [
  "#1C1917",
  "#1A362D",
  "#57534E",
  "#B91C1C",
  "#92400E",
  "#1E3A5F",
  "#7C2D12",
  "#FFFFFF",
  "#F4EFE6",
  "#C4A574",
];

export function toEditorHtml(value) {
  if (!value) return "";
  if (/<\/?[a-z][\s\S]*>/i.test(value)) return value;
  return `<p>${String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\n/g, "<br>")}</p>`;
}

function ToolbarButton({ active, onClick, children, title }) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cn(
        "inline-flex h-8 w-8 items-center justify-center rounded text-[#1C1917] transition-colors hover:bg-[#E7E5E4]",
        active && "bg-[#1A362D] text-white hover:bg-[#12271f]"
      )}
    >
      {children}
    </button>
  );
}

/**
 * Remount with a new `key` when loading a different document so content resets cleanly.
 * onChange receives HTML string (empty string when cleared).
 */
export default function RichTextEditor({
  value = "",
  onChange,
  placeholder = "Write something beautiful…",
  className,
  minHeight = "120px",
  "data-testid": testId,
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Underline,
      TextStyle,
      Color,
      FontFamily,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({ placeholder }),
    ],
    content: toEditorHtml(value),
    editorProps: {
      attributes: {
        class: "rich-text-editor focus:outline-none px-3 py-2",
        style: `min-height: ${minHeight}`,
        ...(testId ? { "data-testid": testId } : {}),
      },
    },
    onUpdate: ({ editor: ed }) => {
      onChange?.(ed.isEmpty ? "" : ed.getHTML());
    },
  });

  if (!editor) return null;

  return (
    <div className={cn("overflow-hidden rounded-md border border-[#E5E5E5] bg-white", className)}>
      <div className="flex flex-wrap items-center gap-1 border-b border-[#E5E5E5] bg-[#FAFAF9] px-2 py-1.5">
        <ToolbarButton title="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}>
          <Bold className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton title="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <Italic className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton title="Underline" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}>
          <UnderlineIcon className="h-3.5 w-3.5" />
        </ToolbarButton>
        <span className="mx-1 h-5 w-px bg-[#E5E5E5]" />
        <select
          className="h-8 max-w-[150px] rounded border border-[#E5E5E5] bg-white px-1.5 text-xs"
          value={editor.getAttributes("textStyle").fontFamily || ""}
          onChange={(e) => {
            const v = e.target.value;
            if (!v) editor.chain().focus().unsetFontFamily().run();
            else editor.chain().focus().setFontFamily(v).run();
          }}
          title="Font"
        >
          <option value="">Default font</option>
          {FONTS.map((f) => (
            <option key={f.label} value={f.value}>{f.label}</option>
          ))}
        </select>
        <span className="mx-1 h-5 w-px bg-[#E5E5E5]" />
        {COLORS.map((c) => (
          <button
            key={c}
            type="button"
            title={c}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().setColor(c).run()}
            className={cn(
              "h-5 w-5 rounded-full border border-[#D6D3D1]",
              editor.isActive("textStyle", { color: c }) && "ring-2 ring-[#1A362D] ring-offset-1"
            )}
            style={{ background: c }}
          />
        ))}
        <label className="ml-1 inline-flex h-8 items-center gap-1 rounded border border-[#E5E5E5] bg-white px-1.5 text-[10px] text-[#57534E]">
          Custom
          <input
            type="color"
            className="h-5 w-5 cursor-pointer border-0 bg-transparent p-0"
            onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
          />
        </label>
        <span className="mx-1 h-5 w-px bg-[#E5E5E5]" />
        <ToolbarButton title="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <List className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton title="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <ListOrdered className="h-3.5 w-3.5" />
        </ToolbarButton>
        <span className="mx-1 h-5 w-px bg-[#E5E5E5]" />
        <ToolbarButton title="Align left" active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()}>
          <AlignLeft className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton title="Align center" active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()}>
          <AlignCenter className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton title="Align right" active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()}>
          <AlignRight className="h-3.5 w-3.5" />
        </ToolbarButton>
        <span className="mx-1 h-5 w-px bg-[#E5E5E5]" />
        <ToolbarButton title="Undo" onClick={() => editor.chain().focus().undo().run()}>
          <Undo2 className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton title="Redo" onClick={() => editor.chain().focus().redo().run()}>
          <Redo2 className="h-3.5 w-3.5" />
        </ToolbarButton>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
